import { useEffect, useMemo, useState } from "react";
import { useCommonFilterOptions, resolveMetadataDropdownApiKey } from "libs/context/CommonFilterOptionsContext";
import { useFilterPreferences } from "libs/context/FilterPreferencesContext";
import {
  buildEmptyFilterFromMeta,
  countActiveMetadataFilters,
  mapFilterDefinitionsToModalData,
  splitMetadataFiltersByDefault,
} from "helpers/metadataFilterHelpers";

/**
 * Splits context/API filter metadata into toolbar vs modal slices and loads remote dropdown options.
 * When `pageName` is provided, user filter toolbar preferences override metadata `isDefault`.
 */
export function useMetadataDrivenFilters(
  metadataFiltersRaw,
  listFilter,
  pageName = ""
) {
  const { getDropdownOptionsByApiKey } = useCommonFilterOptions() || {};
  const [toolbarDropdownReactSelectOptionsByFieldName, setToolbarDropdownReactSelectOptionsByFieldName] =
    useState({});
  const resolvedPageName = String(pageName ?? "").trim();

  const filterPreferences = useFilterPreferences(resolvedPageName, metadataFiltersRaw);
  const fallbackSplit = useMemo(
    () => splitMetadataFiltersByDefault(metadataFiltersRaw),
    [metadataFiltersRaw]
  );

  const {
    all,
    toolbar,
    modalOnly,
    usesMetadataDrivenFilterUi,
    toolbarFieldOrder,
    advancedFieldOrder,
  } = resolvedPageName && filterPreferences.usesMetadataDrivenFilterUi
    ? filterPreferences
    : fallbackSplit;

  const shouldShowTableHeaderFilterAndResetControls = useMemo(
    () => !usesMetadataDrivenFilterUi || modalOnly.length > 0,
    [usesMetadataDrivenFilterUi, modalOnly]
  );

  const shouldRenderMetadataToolbarFilterRow = toolbar.length > 0;

  const modalFilterData = useMemo(
    () => mapFilterDefinitionsToModalData(modalOnly, listFilter),
    [modalOnly, listFilter]
  );

  const activeModalFilterCount = useMemo(
    () => countActiveMetadataFilters(listFilter, modalOnly),
    [listFilter, modalOnly]
  );

  const buildBaselineEmptyListFilter = useMemo(
    () => () => buildEmptyFilterFromMeta(all),
    [all]
  );

  useEffect(() => {
    const toolbarDropdownDefinitions = toolbar.filter((definitionRow) => {
      const controlType = String(definitionRow?.type || "").toLowerCase();
      return (
        (controlType === "dropdown" || controlType === "multiselect") &&
        definitionRow?.field
      );
    });
    if (!toolbarDropdownDefinitions.length) {
      setToolbarDropdownReactSelectOptionsByFieldName({});
      return;
    }
    let cancelled = false;
    (async () => {
      const tuples = await Promise.all(
        toolbarDropdownDefinitions.map(async (definitionRow) => {
          const fieldKey = definitionRow.field;
          const staticOptions = Array.isArray(definitionRow?.itemArray)
            ? definitionRow.itemArray
            : [];
          if (staticOptions.length) return [fieldKey, staticOptions];
          const fetched = getDropdownOptionsByApiKey
            ? await getDropdownOptionsByApiKey(
                resolveMetadataDropdownApiKey(definitionRow),
                { pageName: resolvedPageName }
              )
            : [];
          return [fieldKey, fetched];
        })
      );
      if (cancelled) return;
      const byField = {};
      tuples.forEach(([fieldKey, options]) => {
        byField[fieldKey] = options;
      });
      setToolbarDropdownReactSelectOptionsByFieldName(byField);
    })();
    return () => {
      cancelled = true;
    };
  }, [toolbar, getDropdownOptionsByApiKey, resolvedPageName]);

  return {
    metadataFiltersAll: all,
    metadataToolbarFilterDefinitions: toolbar,
    metadataModalOnlyFilterDefinitions: modalOnly,
    metadataToolbarFieldOrder: toolbarFieldOrder,
    metadataAdvancedFieldOrder: advancedFieldOrder,
    usesMetadataDrivenFilterUi,
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow,
    modalFilterData,
    activeModalFilterCount,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
    handleFilterToolbarDragEnd: filterPreferences.handleFilterDragEnd,
    resetFilterToolbarPreferences: filterPreferences.resetFilterPreferences,
  };
}
